import React, {useEffect, useMemo, useRef} from 'react'
import PropTypes from 'prop-types'
import {Box, Heading, Link, Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage} from 'react-intl'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {KS_PERSONAL_PRIVACY_POLICY_URL, KS_PERSONAL_TERMS_CONDITIONS_URL} from '../../../constants'

const ELIGIBILITY_SCRIPT_ID = 'attn-waitlist-eligibility'
const TRIGGER_ID = 'attn-waitlist-trigger'

const buildEligibilityMap = (product, variantId) => {
    const map = {}
    const variants = product?.variants || []

    if (variants.length) {
        variants.forEach((variant) => {
            if (variant?.productId) {
                map[variant.productId] = !variant.orderable
            }
        })
        return map
    }

    if (variantId) {
        map[variantId] = true
    }

    return map
}

const getProductImage = (product) =>
    product?.imageGroups?.[0]?.images?.[0]?.disBaseLink ||
    product?.imageGroups?.[0]?.images?.[0]?.link

const fireAttentiveProductView = (product, variant, selectedVariantId) => {
    if (typeof window.attentive?.analytics?.productView !== 'function' || !selectedVariantId) {
        return false
    }

    window.attentive.analytics.productView({
        items: [
            {
                productId: product?.c_masterVariantID || product?.master?.masterId || product?.id,
                productVariantId: selectedVariantId,
                name: product?.name,
                productImage: getProductImage(product),
                price: {
                    value: String(variant?.price || product?.price || ''),
                    currency: product?.c_selectedCurrency || 'USD'
                },
                quantity: 1
            }
        ]
    })
    return true
}

const AttentiveBackInStock = ({product, variant}) => {
    const wrapperRef = useRef(null)
    const selectedVariantId = variant?.productId || product?.id
    const {
        app: {commerceAPI}
    } = getConfig()
    const {hostUrl} = commerceAPI.storefront
    const eligibility = useMemo(
        () => buildEligibilityMap(product, selectedVariantId),
        [product?.variants, selectedVariantId]
    )

    useEffect(() => {
        const wrapper = wrapperRef.current
        if (!wrapper) {
            return
        }

        let script = wrapper.querySelector(`#${ELIGIBILITY_SCRIPT_ID}`)
        if (!script) {
            script = document.createElement('script')
            script.id = ELIGIBILITY_SCRIPT_ID
            script.type = 'application/json'
            const trigger = wrapper.querySelector(`#${TRIGGER_ID}`)
            wrapper.insertBefore(script, trigger)
        }
        script.textContent = JSON.stringify(eligibility)
    }, [eligibility])

    useEffect(() => {
        return () => {
            document.getElementById(ELIGIBILITY_SCRIPT_ID)?.remove()
        }
    }, [])

    useEffect(() => {
        if (!selectedVariantId) {
            return undefined
        }

        let cancelled = false
        let attempts = 0
        const maxAttempts = 40

        const fire = () => {
            if (cancelled) {
                return true
            }
            return fireAttentiveProductView(product, variant, selectedVariantId)
        }

        if (fire()) {
            return undefined
        }

        const intervalId = window.setInterval(() => {
            attempts += 1
            if (fire() || attempts >= maxAttempts) {
                window.clearInterval(intervalId)
            }
        }, 250)

        return () => {
            cancelled = true
            window.clearInterval(intervalId)
        }
    }, [selectedVariantId, product, variant])

    return (
        <Box ref={wrapperRef} className="attn-oos" data-testid="sf-attentive-back-in-stock">
            <Heading
                className="attn-oos"
                fontSize="23px"
                fontWeight="400"
                marginBottom="10px"
                letterSpacing="0.115px"
                lineHeight="normal"
                color="black.100"
            >
                {!product?.c_comingSoonProduct ? (
                    <FormattedMessage id="sold.out" defaultMessage="Sold Out" />
                ) : (
                    product?.c_comingSoonFormTitle || (
                        <FormattedMessage
                            id="back.in.stock.heading"
                            defaultMessage="Shop it first!"
                        />
                    )
                )}
            </Heading>
            <Text
                fontSize="16px"
                fontWeight="300"
                letterSpacing="0.08px"
                lineHeight="25px"
                color="black.100"
            >
                {product?.c_comingSoonProduct && product?.c_comingSoonFormDesc ? (
                    product?.c_comingSoonFormDesc
                ) : (
                    <FormattedMessage
                        id="back.in.stock.description"
                        defaultMessage="Sign up to our mailing list and be notified when this style becomes available."
                    />
                )}
            </Text>
            <Text
                marginTop="20px"
                color="black.100"
                fontSize="13px"
                textUnderlineOffset="4px"
                lineHeight="22px"
                fontWeight="300"
                letterSpacing="0.065px"
            >
                {product?.c_comingSoonProduct ? (
                    <FormattedMessage
                        id="join.the.wait.list.join_the_waitlist"
                        defaultMessage="By clicking Join the Waitlist, you agree to Kendra Scott’s "
                    />
                ) : (
                    <FormattedMessage
                        id="join.the.wait.list.agreement_notify_me"
                        defaultMessage="By clicking Notify Me, you agree to Kendra Scott’s "
                    />
                )}
                <Text as="span" display="block">
                    <Link href={hostUrl + KS_PERSONAL_TERMS_CONDITIONS_URL} variant="underline">
                        <FormattedMessage
                            id="join.the.wait.list.terms_conditions"
                            defaultMessage="Terms & Conditions"
                        />
                    </Link>{' '}
                    <FormattedMessage id="join.the.wait.list.and" defaultMessage="and" />{' '}
                    <Link href={hostUrl + KS_PERSONAL_PRIVACY_POLICY_URL} variant="underline">
                        <FormattedMessage
                            id="join.the.wait.list.privacy_policy"
                            defaultMessage="Privacy Policy"
                        />
                    </Link>
                    {'.'}
                </Text>
            </Text>
            <input type="hidden" name="id" value={selectedVariantId || ''} readOnly />
            <div id={TRIGGER_ID} className="attn-oos" />
        </Box>
    )
}

AttentiveBackInStock.propTypes = {
    product: PropTypes.object,
    variant: PropTypes.object
}

export default AttentiveBackInStock
